import { v4 as uuidv4 } from "uuid";
import { startBacktest } from "./BacktestStrategy";
import { apiRunBacktest } from "api/apiPredictions";

export const BACKTEST_MODES = {
  CLIENT: "client",
  SERVER: "server",
};

// Flip this to SERVER once the server path has proven itself (fast/smooth
// enough) on real traffic -- at that point BacktestStrategy.js and the CLIENT
// branch below can be deleted entirely.
export const DEFAULT_BACKTEST_MODE = BACKTEST_MODES.CLIENT;

// Everything startBacktest()/backend backtest.js expects on backtestSettings,
// minus predictionsTable (the server fetches predictions itself from
// symbol/models/from/to instead). Kept as an explicit allowlist so a future
// field added to one call site can't silently go missing from the other.
const SETTINGS_KEYS = [
  "takeProfitPercent",
  "stopLossPercent",
  "enterPositionBuyCount",
  "minPredictionProba",
  "exitPositionNoBuyCount",
  "daysOpenExitCount",
  "initialBalance",
  "orderSize",
  "orderSizeType",
  "maxOpenOrders",
  "resetTrendAfterSell",
  "probaSizingEnabled",
  "probaSizingBoostThreshold",
  "userCommission",
  "premiumPerShare",
  "slippage",
  "startDate",
  "endDate",
];

const pickSettings = (backtestSettings) =>
  SETTINGS_KEYS.reduce((acc, key) => {
    acc[key] = backtestSettings[key];
    return acc;
  }, {});

/**
 * Runs a backtest either locally (the client engine, daily-only) or on the
 * server (the same engine, optionally driven minute-by-minute against 1m
 * candles for intraday take profit / stop loss / trailing take profit --
 * something the client engine can never do, since it only ever sees daily
 * OHLC rows). Both branches resolve to the same strategy shape, so every
 * consumer (AiBacktestTable, AccProfitChart, the Cards/*, TVChartContainer,
 * MonteCarloSimulation, ...) keeps working unchanged regardless of mode.
 *
 * Enabling intraday always runs on the server -- there is no client-side
 * intraday engine.
 *
 * @param {object} backtestSettings - same shape startBacktest() takes, including predictionsTable
 * @param {object} [opts]
 * @param {"client"|"server"} [opts.mode]
 * @param {object} [opts.intraday] - { enabled, regularSessionOnly, takeProfit, stopLoss, trailingTakeProfit }
 * @param {string} [opts.from] - predictions fetch range start, e.g. results.startDate (server mode only)
 * @param {string} [opts.to] - predictions fetch range end, e.g. results.endDate (server mode only)
 * @param {string[]} [opts.models] - model ids, e.g. results.selectedModels (server mode only)
 * @param {string} [opts.uuid] - socket progress channel; subscribe to it with
 *   socket.on(uuid, ...) BEFORE calling this, since progress events can arrive
 *   while the request is still in flight. A fresh one is minted if omitted.
 * @param {AbortSignal} [opts.signal] - aborts a superseded request (server mode only)
 */
export const runBacktest = async (backtestSettings, opts = {}) => {
  const { mode = DEFAULT_BACKTEST_MODE, intraday } = opts;

  if (mode === BACKTEST_MODES.CLIENT && !intraday?.enabled) {
    return startBacktest(backtestSettings);
  }

  const uuid = opts.uuid || uuidv4();
  const { strategy } = await apiRunBacktest(
    {
      uuid,
      symbol: backtestSettings.symbol,
      models: opts.models,
      from: opts.from,
      to: opts.to,
      settings: pickSettings(backtestSettings),
      intraday,
    },
    opts.signal
  );

  // predictions is intentionally omitted from the server response (the
  // client already holds this array) -- re-attach it and mint a fresh _id,
  // exactly like startBacktest() does, so downstream consumers can't tell
  // which engine produced the result.
  return {
    ...strategy,
    _id: uuidv4(),
    predictions: backtestSettings.predictionsTable,
  };
};
